import { UI, Stack, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { getHomeValueVisibility } from '../../../../../../store/home/capture/selector';
import { customersActivation } from '../../../../../../store/home/conversion/selector';
import { PerformanceSummaryMetric } from '../../../PerformanceSummaryMetric';

export interface CustomersActivationContentProps {
  isMobile?: boolean;
}

export const CustomersActivationContent: React.FC<CustomersActivationContentProps> = ({
  isMobile,
}) => {
  const valueVisible = useRecoilValue(getHomeValueVisibility);

  const {
    data: customersActivationData,
    loading: customersActivationLoading,
    refresher: refresherCustomersActivation,
    error: errorCustomersActivation,
  } = useRecoilQuery(customersActivation);

  const charContent = useMemo(() => {
    const bars = [
      {
        color: 'brand.secondary.dark',
        label: 'Qtd. ativações',
        dataKey: 'activationValue',
      } as const,
    ];
    const additionalData = {
      year: customersActivationData?.data?.performanceYear ?? 2024,
      tooltipLabels: [...bars],
    };

    if (errorCustomersActivation) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ErrorBoundary
            onRefresh={refresherCustomersActivation}
            refreshText="RECARREGAR"
            refreshButtonCustomProps={{
              variant: 'textLink',
              rightIcon: (
                <UI.Icon name="Refresh" size="xxs" color={Color['brand.tertiary.principal']} />
              ),
            }}
          />
        </Stack>
      );
    }

    if (customersActivationLoading) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ChartBarsLoading isMobile={isMobile} barsLength={12} />
        </Stack>
      );
    }

    return (
      <UI.NewCustomersActivationChart
        data={customersActivationData?.data.performanceData ?? []}
        additionalData={additionalData}
        bars={bars}
        isMobile={isMobile}
      />
    );
  }, [
    customersActivationData,
    customersActivationLoading,
    errorCustomersActivation,
    isMobile,
    refresherCustomersActivation,
  ]);

  const bigNumbers = customersActivationData?.data.bigNumbers;

  return (
    <VStack space={Spacing['stack.xxs']} direction={isMobile ? 'column-reverse' : 'column'}>
      <Stack
        space={Spacing['stack.xxs']}
        justifyContent="space-between"
        px={Spacing['inline.xxs']}
        direction={isMobile ? 'column' : 'row'}>
        <PerformanceSummaryMetric
          label="YTD"
          value={bigNumbers?.yearToDateValue ?? 0}
          performanceComparisonLabel="YoY"
          performanceComparisonValue={bigNumbers?.previousYearValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.yoYPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationLoading}
          withError={!!errorCustomersActivation}
        />
        <PerformanceSummaryMetric
          label="QTD"
          value={bigNumbers?.quarterToDateValue ?? 0}
          performanceComparisonLabel="QoQ"
          performanceComparisonValue={bigNumbers?.previousQuarterValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.qoQPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationLoading}
          withError={!!errorCustomersActivation}
        />
        <PerformanceSummaryMetric
          label="MTD"
          value={bigNumbers?.monthToDateValue ?? 0}
          performanceComparisonLabel="MoM"
          performanceComparisonValue={bigNumbers?.previousMonthValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.moMPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationLoading}
          withError={!!errorCustomersActivation}
        />
        <PerformanceSummaryMetric
          label="WTD"
          value={bigNumbers?.weekToDateValue ?? 0}
          performanceComparisonLabel="WoW"
          performanceComparisonValue={bigNumbers?.previousWeekValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.woWPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationLoading}
          withError={!!errorCustomersActivation}
        />
      </Stack>
      {charContent}
    </VStack>
  );
};
